import { motion } from 'framer-motion';
import { Clock, AlertTriangle, XCircle, ShieldAlert } from 'lucide-react';
import { CheckoutShell } from './CheckoutShell.jsx';
import { formatMoney, formatExactTimestamp } from '../../lib/format.js';

/**
 * HC-10: "Every outcome is handled explicitly." This component is that table,
 * rendered. Each entry says what the customer sees and what to do next —
 * including "do nothing", which is the correct answer for an unconfirmed payment.
 *
 * The motion here is doing a job: a tick that draws itself reads as *completion*
 * in a way a static tick does not, and it buys the half-second before a redirect
 * in which a customer decides whether their money arrived safely.
 *
 * Every element is nonetheless laid out visible and animates only its position.
 * A receipt that needs an animation frame to become readable is the wrong bet on
 * the one screen where a customer is deciding whether their money is safe — if
 * the frame never comes, they must still see that they paid.
 */
const EASE = [0.2, 0.7, 0.2, 1];

/** A stroke-drawn tick. The path animates, so it feels resolved, not stamped. */
function DrawnCheck() {
  return (
    <svg viewBox="0 0 52 52" className="h-full w-full" aria-hidden="true">
      <motion.circle
        cx="26" cy="26" r="23" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.25"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
      />
      {/* Static fallback, in case the draw never runs. */}
      <path
        d="M15 26.5 L23 34 L37.5 19" fill="none" stroke="currentColor"
        strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.18"
      />
      <motion.path
        d="M15 26.5 L23 34 L37.5 19" fill="none" stroke="currentColor"
        strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 0.4, delay: 0.22, ease: EASE }}
      />
    </svg>
  );
}

const OUTCOMES = {
  approved: {
    tone: 'success', drawn: true,
    title: 'Payment complete',
    body: 'Thank you. A receipt is on its way to your email.',
  },
  review: {
    icon: Clock, tone: 'warning',
    title: 'This payment is being reviewed',
    body: 'Your card details were accepted and the payment is being checked. The merchant will be in touch shortly. Please don’t pay again.',
  },
  unconfirmed: {
    icon: Clock, tone: 'warning',
    title: 'We’re confirming this payment',
    /**
     * HC-11: we cannot know whether the card was charged, so the one thing that
     * must not happen is the customer paying a second time.
     */
    body: 'The payment network is taking longer than usual to confirm. Please don’t pay again — the merchant will contact you once it settles, and you will not be charged twice.',
  },
  /**
   * A confirmation is already running for this link — the other half of a
   * double-click, or a refresh while the bank is deciding. Not a failure, and
   * not a reason to pay again.
   */
  in_progress: {
    icon: Clock, tone: 'warning',
    title: 'This payment is being processed',
    body: 'Please wait a moment and don’t pay again. If you refreshed the page, your payment is still going through.',
  },
  expired: {
    icon: AlertTriangle, tone: 'neutral',
    title: 'This checkout link has expired',
    body: 'Nothing has been charged. Ask the merchant for a new link to complete your payment.',
  },
  cancelled: {
    icon: XCircle, tone: 'neutral',
    title: 'This checkout was cancelled',
    body: 'Nothing has been charged. Ask the merchant for a new link if you still want to pay.',
  },
  /**
   * The offline outcome. Deliberately not a tick: nothing has been received yet,
   * and a customer who reads "complete" here will not send the transfer.
   */
  awaiting_confirmation: {
    icon: Clock, tone: 'warning',
    title: 'Waiting for your payment',
    body: 'We have recorded your order. The merchant will confirm it once your payment reaches them.',
  },
  already_paid: {
    tone: 'success', drawn: true,
    title: 'This payment is already complete',
    body: 'No further action is needed, and you have not been charged twice.',
  },
  no_attempts_remaining: {
    icon: ShieldAlert, tone: 'neutral',
    title: 'Too many attempts on this link',
    body: 'For security, this checkout is now closed. Nothing has been charged — please ask the merchant for a new link.',
  },
  declined: {
    icon: XCircle, tone: 'danger',
    title: 'That payment could not be completed',
    body: 'Nothing has been charged. Please ask the merchant for a new link to try another card.',
  },
};

const TONES = {
  success: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-600 border-red-200',
  neutral: 'bg-surface-2 text-faint border-line',
};

export function CheckoutOutcome({ merchant, result, message, payment, returnUrl, extra }) {
  const outcome = OUTCOMES[result] ?? OUTCOMES.declined;
  const Icon = outcome.icon;
  const showReceipt = result === 'approved' || result === 'already_paid';
  const failed = outcome.tone === 'danger';

  const rows = [
    ['Amount', formatMoney(payment?.amount, payment?.currency), 'tabular font-semibold text-ink'],
    payment?.card?.last4 && [
      'Card',
      <span className="capitalize">{payment.card.brand} ···· {payment.card.last4}</span>,
      'text-ink-soft',
    ],
    payment?.orderId && ['Order', payment.orderId, 'text-ink-soft'],
    payment?.id && ['Reference', <span className="font-mono text-xs">{payment.id}</span>, 'text-ink-soft'],
    payment?.succeededAt && ['Paid', formatExactTimestamp(payment.succeededAt), 'text-ink-soft'],
  ].filter(Boolean);

  return (
    <CheckoutShell merchant={merchant} payment={payment} showTrust={false} bare>
      <motion.div
        className="flex min-h-screen flex-col justify-center px-6 py-10"
        initial={{ y: 10 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <motion.div
          className={`mx-auto grid h-14 w-14 place-items-center rounded-2xl border ${TONES[outcome.tone]}`}
          initial={{ scale: 0.82 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.34, ease: EASE, delay: 0.05 }}
        >
          {outcome.drawn ? (
            <div className="h-8 w-8"><DrawnCheck /></div>
          ) : (
            // A failure gets a single decisive shake, never a loop.
            <motion.span
              animate={failed ? { x: [0, -5, 4, -3, 0] } : {}}
              transition={{ duration: 0.4, delay: 0.24 }}
            >
              <Icon className="h-6 w-6" strokeWidth={2.2} aria-hidden="true" />
            </motion.span>
          )}
        </motion.div>

        <motion.h1
          className="mt-5 text-center text-xl font-semibold tracking-tight text-ink"
          initial={{ y: 6 }} animate={{ y: 0 }}
          transition={{ duration: 0.3, delay: 0.14, ease: EASE }}
        >
          {outcome.title}
        </motion.h1>

        <motion.p
          className="mx-auto mt-2 max-w-sm text-center text-sm leading-relaxed text-muted"
          initial={{ y: 6 }} animate={{ y: 0 }}
          transition={{ duration: 0.3, delay: 0.2, ease: EASE }}
        >
          {message && message !== outcome.title ? message : outcome.body}
        </motion.p>

        {/* Method-specific detail — the bank reference an offline payer must quote. */}
        {extra && (
          <motion.div
            className="mt-6"
            initial={{ y: 8 }} animate={{ y: 0 }}
            transition={{ duration: 0.32, delay: 0.26, ease: EASE }}
          >
            {extra}
          </motion.div>
        )}

        {/* HC-10: a success page with a receipt. */}
        {showReceipt && payment && (
          <motion.dl
            className="mt-7 divide-y overflow-hidden rounded-xl border"
            style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
            initial={{ y: 10 }} animate={{ y: 0 }}
            transition={{ duration: 0.34, delay: 0.3, ease: EASE }}
          >
            {rows.map(([term, value, cls], i) => (
              <motion.div
                key={term}
                className="flex items-baseline justify-between gap-4 px-4 py-2.5"
                initial={{ x: 6 }} animate={{ x: 0 }}
                transition={{ duration: 0.22, delay: 0.36 + i * 0.05 }}
              >
                <dt className="text-xs text-muted">{term}</dt>
                {/* HC-7: brand and last four are all we keep — enough for a receipt. */}
                <dd className={`min-w-0 truncate text-right text-sm ${cls}`}>{value}</dd>
              </motion.div>
            ))}
          </motion.dl>
        )}

        {returnUrl && result === 'approved' && (
          <motion.p
            className="mt-5 text-center text-xs text-muted"
            initial={{ y: 4 }} animate={{ y: 0 }} transition={{ delay: 0.7 }}
          >
            Taking you back to the store…{' '}
            <a href={returnUrl} className="font-medium text-ink-soft underline underline-offset-2 hover:text-ink">
              Go now
            </a>
          </motion.p>
        )}
      </motion.div>
    </CheckoutShell>
  );
}

export default CheckoutOutcome;
